import React from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { formatDate, createOverflow } from '../../lib/format';

import Title from '../shared/Title';
import { FourFactors } from '../shared/SummaryList';
import Pagination from '../shared/Pagination';
import { FOUR_FACTORS } from '../../shared/constants';
import Spinner from '../shared/Spinner';
import { teamNameShort } from '../../lib/format';

const GamesSummary = ({
  team,
  gamesByMonth,
  pathTo,
  currentPage,
  pageSize,
  itemsCount,
  onPageChange,
  season,
}) => {
  const isLoading = !gamesByMonth;
  const overflow = createOverflow(team, season);

  return (
    <div className="s-games">
      <Title
        team={team}
        linkTo={pathTo('team')}
        subtitle="Games"
        overflow={overflow}
      />

      <div className="summary-box__list">
        {isLoading ? (
          <Spinner />
        ) : (
          gamesByMonth.map(month => (
            <div className="summary-box" key={month.name}>
              <p className="summary-box__title">{month.name}</p>
              {month.games.map(game => (
                <Link
                  key={game.id}
                  to={pathTo('game-explorer', game)}
                  className={
                    game.ignored
                      ? 'summary-box__item summary-box__item__ignored'
                      : 'summary-box__item'
                  }
                >
                  <GameSummary game={game} team={team} />
                  <div className="summary-box__ff-wrapper">
                    <SummaryStats game={game} />
                  </div>
                </Link>
              ))}
            </div>
          ))
        )}
      </div>

      <Pagination
        current={currentPage}
        totalItems={itemsCount}
        pageSize={pageSize}
        onChange={onPageChange}
      />
    </div>
  );
};

GamesSummary.propTypes = {
  team: PropTypes.object,
  gamesByMonth: PropTypes.array,
};

export default GamesSummary;

// FIXME: refactor with a SummaryList
const GameSummary = ({ game, team }) => {
  const winner = isWinner(game, team);

  return (
    <div className="summary-box__item-container">
      <div className="summary-box__team-summary">
        <div className="summary-box__team-summary__side">
          <img src={game.visitor.team.image} alt={''}></img>
          <div className="visitor">
            <span
              className={`team-name ${game.home_points < game.visitor_points &&
                'winner'}`}
            >
              {game.visitor.team.name}
            </span>
            <div className="summary-box__team-summary__conference">
              {game.visitor.team.conference
                ? `${game.visitor.team.conference.division.name} - ${game.visitor.team.conference.name}`
                : ``}
            </div>
          </div>
        </div>
        <div className="summary-box__team-summary__score">
          <div className="summary-box__team-summary__score-points">
            <div
              className={`summary-box__points ${game.visitor_points >
                game.home_points && 'winner'}`}
            >
              {game.visitor_points}
            </div>{' '}
            ·
            <div
              className={`summary-box__points ${game.visitor_points <
                game.home_points && 'winner'}`}
            >
              {game.home_points}
            </div>
          </div>
          <div className={`win-loss ${winner ? 'win' : 'loss'}`}>
            {winner ? 'W' : 'L'}
          </div>
        </div>
        <div className="summary-box__team-summary__side">
          <img className="right" src={game.home.team.image} alt={''}></img>
          <div>
            <span
              className={`team-name ${game.home_points > game.visitor_points &&
                'winner'}`}
            >
              {game.home.team.name}
            </span>
            <div className="summary-box__team-summary__conference">
              {game.home.team.conference
                ? `${game.home.team.conference.division.name} - ${game.home.team.conference.name}`
                : ``}
            </div>
          </div>
        </div>
      </div>
      <div className="summary-box__item-desc">
        {game.stage.description} · {formatDate(game.start_time)}
        {game.ignored && (
          <div>
            <strong>
              * Shift metrics, Advanced Player and lineup metrics for this game
              <br />
              are unavailable due to incomplete source data.
            </strong>
          </div>
        )}
      </div>
      {!game.profile && (
        <div className="summary-box__item-warning">
          ! Shooting profiles unavailable due to incomplete source data.
        </div>
      )}
    </div>
  );
};

const SummaryStats = ({ game }) => {
  const factors = getGameFactors(game);

  return <FourFactors factors={factors} />;
};

const getGameFactors = game => {
  const { homeFactors, visitorFactors } = buildFourFactors(game);

  return [
    {
      title: game.home.team.name,
      name: teamNameShort(game.home.team),
      data: homeFactors,
    },
    {
      title: game.visitor.team.name,
      name: teamNameShort(game.visitor.team),
      data: visitorFactors,
    },
  ];
};

const isWinner = (game, team) => {
  const teamScore =
    game.home.team.id === team.id ? game.home_points : game.visitor_points;
  const otherScore =
    game.home.team.id === team.id ? game.visitor_points : game.home_points;
  return teamScore > otherScore;
};

const buildFourFactors = game => {
  const { homeFactors, visitorFactors } = setBestFactors(
    getFourFactors(game.home.team.id, game),
    getFourFactors(game.visitor.team.id, game),
  );

  return {
    homeFactors,
    visitorFactors,
  };
};

// Selectors: https://www.swingman.space/api/team/35/games/stats
const getStats = (teamId, game) =>
  game.game_stats.find(s => s.team_id === teamId) || {};

const getFourFactors = (teamId, game) =>
  getStats(teamId, game).four_factors || {};

const setBestFactors = (home, visitor) => {
  const homeFactors = {};
  const visitorFactors = {};

  FOUR_FACTORS.forEach(factor => {
    const { isHomeBest, isVisitorBest } = compareHomeVisitorFactor(
      factor,
      home[factor],
      visitor[factor],
    );

    homeFactors[factor] = {
      value: home[factor],
      isBest: isHomeBest,
    };
    visitorFactors[factor] = {
      value: visitor[factor],
      isBest: isVisitorBest,
    };
  });

  return { homeFactors, visitorFactors };
};

const compareHomeVisitorFactor = (factor, homeFactor, visitorFactor) => {
  if (homeFactor === visitorFactor) {
    return { isHomeBest: true, isVisitorBest: true };
  }

  const isHomeBest =
    factor === 'tov' ? homeFactor < visitorFactor : homeFactor > visitorFactor;

  return { isHomeBest: isHomeBest, isVisitorBest: !isHomeBest };
};
